import React from "react";
import { T } from "../../app/tokens.js";
import { seatGroups, STATUS_LABEL } from "./capacityModel.js";
import { SEAT_STYLE, SEAT_SIZE, SEAT_GAP, statusPillStyle } from "./capacityStyles.js";

const LABEL = { used: "used", spare: "spare", densUsed: "densify used", densRoom: "densify room", over: "over" };
const ORDER = ["used", "spare", "densUsed", "densRoom", "over"];
const r = (n) => (Number.isFinite(n) ? Math.round(n) : "\u2013");
const SUMMARY_COLOR = { spare: T.greenDeep, above: T.teal, over: T.ragRed };

export function SeatGrid({ title, evalResult, testId }) {
  const g = evalResult ? seatGroups(evalResult.demand, evalResult.seats, evalResult.max, evalResult.installedSeats) : null;
  const installed = Number.isFinite(evalResult?.installedSeats) ? Math.max(0, Math.round(evalResult.installedSeats)) : null;
  let represented = 0;
  let boundaryDrawn = false;
  const square = (k, count, key, remainder = false) => {
    const start = represented;
    represented += count;
    const boundary = !boundaryDrawn && installed != null && installed >= start && installed <= represented;
    if (boundary) boundaryDrawn = true;
    return <i key={key} data-seat={k} data-seat-count={count} data-seat-start={start}
      data-remainder={remainder ? count : undefined}
      role={remainder ? "img" : undefined}
      aria-label={remainder ? `${count} ${LABEL[k]} seat${count === 1 ? "" : "s"} (partial square)` : undefined}
      title={remainder ? `${count} of ${g.per} seats` : undefined}
      style={{ position: "relative", flex: "none", width: SEAT_SIZE * count / g.per, height: SEAT_SIZE, borderRadius: 1.5, boxSizing: "border-box", ...SEAT_STYLE[k] }}>
      {boundary && <span data-testid={`${testId}-installed-divider`} data-installed-seats={installed} data-fraction={(installed - start) / count}
        aria-hidden="true" style={{ position: "absolute", top: -1, height: SEAT_SIZE, left: SEAT_SIZE * (installed - start) / g.per - 2, width: 0, borderLeft: `2px solid ${T.dark}`, zIndex: 1 }} />}
    </i>;
  };
  return (
    <div data-testid={testId} style={{ flex: "1 1 260px", minWidth: 0 }}>
      <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.dark, marginBottom: 6 }}>
        {title} {evalResult && <span data-testid={`${testId}-status`} style={statusPillStyle(evalResult.status)}>{STATUS_LABEL[evalResult.status]}</span>}
      </div>
      {!g || evalResult.status === "history" || evalResult.status === "no_data" ? (
        <div style={{ fontSize: T.fsXs, color: T.mid }}>
          {evalResult?.status === "history" ? "Under 3 months of headcount history; no reliable projection yet." : "No data for this period."}
        </div>
      ) : (
        <>
          <div data-testid={`${testId}-squares`} style={{ display: "flex", flexWrap: "wrap", gap: SEAT_GAP, width: "100%", maxWidth: 257, alignItems: "center" }}>
            {ORDER.flatMap((k) => {
              const seats = g.counts[k];
              const full = Math.floor(seats / g.per);
              const rem = seats % g.per;
              const out = Array.from({ length: full }, (_, i) => square(k, g.per, `${k}-${i}`));
              // Partial block: width proportional to the seats it holds, with exact count.
              if (rem > 0) out.push(square(k, rem, `${k}-rem`, true));
              return out;
            })}
          </div>
          <div data-testid={`${testId}-summary`} style={{ fontSize: T.fsXs, color: T.mid, marginTop: 6 }}>
            {`${g.demand} / ${g.seats} usable seats (${r(evalResult.installedSeats)} installed)`}{evalResult.recordedMax != null && g.max != null ? ` · max ≈ ${g.max} usable (${r(evalResult.recordedMax)} desks)` : ""} · <b style={{ color: SUMMARY_COLOR[g.summary.kind] }}>{g.summary.text}</b>
            {` · 1 square = ${g.per} seat${g.per === 1 ? "" : "s"}`}{g.partial.length ? "; narrow squares hold the remainder" : ""}
            {installed != null && (installed <= g.total ? " · solid divider = installed seats" : " · installed boundary lies beyond the represented usable seats")}
          </div>
        </>
      )}
    </div>
  );
}
